import { useEffect, useMemo, useState } from 'react';
import usePayoutStructures, { usePayoutBrackets } from '@/hooks/usePayoutStructures';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ConfirmModal from '@/components/ConfirmationModal/ConfirmModal';
import TournamentPayoutStructureForm from './components/TournamentPayoutStructureForm';
import { clearValidationError, setStructureData, setValidationError } from '@/redux/slices/payoutStructure';
import { useDispatch, useSelector } from 'react-redux';
import { CreatePayoutBracketDto } from '@repo/common-types';
import { AppState } from '@/redux/root';
import { PayoutBracketValidationError, validatePayoutBrackets } from '@repo/common-types';

const EditPayoutStructureForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    payoutStructures,
    createOrUpdateRangeBracketsMutation,
    updatePayoutStructureMutation,
    deletePayoutStructureMutation,
  } = usePayoutStructures();

  const { name, isDirty, validationErrors, playersColumns, rankRows } = useSelector(
    (state: AppState) => state.payoutStructure,
  );
  const { payoutStructureId } = useParams<{ payoutStructureId: string }>();

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const selectedPayoutStructure = payoutStructures?.find((payoutStructure) => payoutStructure.id === payoutStructureId);

  const { data: brackets, isLoading: bracketsLoading } = usePayoutBrackets(payoutStructureId ?? '');

  const defaultFormValues = useMemo(() => {
    if (!selectedPayoutStructure || !brackets || brackets.length === 0) {
      return {
        rankRows: [],
        playersColumns: [],
      };
    }

    // First, sort brackets to ensure consistent processing
    const sortedBrackets = [...brackets].sort((a, b) => {
      // Sort by playersFrom first, then by rankFrom
      if (a.playersFrom !== b.playersFrom) {
        return a.playersFrom - b.playersFrom;
      }
      return a.rankFrom - b.rankFrom;
    });

    // Build rankRows first by collecting unique rank ranges and sorting them
    const uniqueRankRanges = new Map<string, { rankFrom: number; rankTo: number }>();

    sortedBrackets.forEach((bracket) => {
      const key = `${bracket.rankFrom}-${bracket.rankTo}`;
      if (!uniqueRankRanges.has(key)) {
        uniqueRankRanges.set(key, {
          rankFrom: bracket.rankFrom,
          rankTo: bracket.rankTo,
        });
      }
    });

    const rankRows = Array.from(uniqueRankRanges.values()).sort((a, b) => a.rankFrom - b.rankFrom);

    // Build playersColumns with proper percentage mapping
    const playersColumnsMap = new Map<string, { playersFrom: number; playersTo: number; percentages: number[] }>();

    sortedBrackets.forEach((bracket) => {
      const columnKey = `${bracket.playersFrom}-${bracket.playersTo}`;

      if (!playersColumnsMap.has(columnKey)) {
        playersColumnsMap.set(columnKey, {
          playersFrom: bracket.playersFrom,
          playersTo: bracket.playersTo,
          percentages: new Array(rankRows.length).fill(0),
        });
      }

      // Find the correct row index for this bracket
      const rowIndex = rankRows.findIndex((row) => row.rankFrom === bracket.rankFrom && row.rankTo === bracket.rankTo);

      if (rowIndex >= 0) {
        const column = playersColumnsMap.get(columnKey)!;
        column.percentages[rowIndex] = bracket.percentage;
      }
    });

    const playersColumns = Array.from(playersColumnsMap.values()).sort((a, b) => a.playersFrom - b.playersFrom);

    return {
      rankRows,
      playersColumns,
      name: selectedPayoutStructure.name,
    };
  }, [selectedPayoutStructure, brackets]);

  // Initialize Redux state from defaultValues
  useEffect(() => {
    if (defaultFormValues.name || defaultFormValues.playersColumns || defaultFormValues.rankRows) {
      dispatch(
        setStructureData({
          name: defaultFormValues.name || '',
          playersColumns: defaultFormValues.playersColumns || [],
          rankRows: defaultFormValues.rankRows || [],
        }),
      );
    }
  }, [dispatch, defaultFormValues]);

  // Validate name uniqueness
  useEffect(() => {
    if (name) {
      const isDuplicate = payoutStructures?.some((structure) => {
        if (structure.id === selectedPayoutStructure?.id) {
          return false;
        }
        return structure.name === name;
      });

      if (isDuplicate) {
        dispatch(setValidationError({ field: 'name', error: 'Name must be unique' }));
      } else {
        dispatch(clearValidationError('name'));
      }
    } else {
      dispatch(clearValidationError('name'));
    }
  }, [name, payoutStructures, selectedPayoutStructure, dispatch]);

  const onSubmit = async () => {
    try {
      if (!payoutStructureId) {
        return;
      }

      if (!playersColumns || !Array.isArray(playersColumns)) {
        toast.error('Invalid form data: playersColumns is missing or invalid');
        return;
      }

      // Check if name was changed by comparing with original value
      if (name !== selectedPayoutStructure?.name) {
        await updatePayoutStructureMutation({
          id: payoutStructureId,
          name: name,
        });
      }

      const formattedBrackets: CreatePayoutBracketDto[] = playersColumns.reduce((acc, curr) => {
        curr.percentages.forEach((percentage, index) => {
          const rankFrom = rankRows[index]?.rankFrom;
          const rankTo = rankRows[index]?.rankTo;

          if (!percentage || Number(percentage) === 0) {
            return;
          }

          acc.push({
            percentage: Number(percentage),
            playersFrom: curr.playersFrom,
            playersTo: curr.playersTo,
            rankFrom,
            rankTo,
          });
        });

        return acc;
      }, []);

      validatePayoutBrackets(formattedBrackets);

      await createOrUpdateRangeBracketsMutation({
        payoutStructureId,
        brackets: formattedBrackets,
      });

      toast.success('Payout structure updated successfully');
    } catch (error: any) {
      if (error instanceof PayoutBracketValidationError) {
        toast.error(error.message);
        return;
      }
      toast.error(error?.response?.data?.message || 'Failed to update payout structure');
    }
  };

  const onDelete = async () => {
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deletePayoutStructureMutation(payoutStructureId);
      setShowConfirmModal(false);
      navigate('/tournament/payout-structures');
      toast.success('Payout structure deleted successfully');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to delete payout structure');
    }
  };

  if (!selectedPayoutStructure) {
    return null;
  }

  if (bracketsLoading) {
    return <div className="flex items-center justify-center p-8">Loading brackets...</div>;
  }

  return (
    <>
      {showConfirmModal && (
        <ConfirmModal
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmDelete}
          title="Delete Payout Structure"
          message="Are you sure you want to delete this payout structure?"
          confirmButtonText="Delete"
          cancelButtonText="Cancel"
        />
      )}
      <TournamentPayoutStructureForm title="Edit Payout Structure" onSubmit={onSubmit} onDelete={onDelete} />
    </>
  );
};

export default EditPayoutStructureForm;
